/**
 * Shared intake contracts for Epic #738 (AI intake accuracy) -- slice S0.
 *
 * ONE definition of the vocabulary every intake slice consumes. Nothing else
 * in the repo may redefine these unions (plan §5 "S0"); consumers import the
 * types for typing and the `*_VALUES` tuples for runtime checks / zod enums.
 * Mirrors in `@batch-and-bowl/validators` land with the first request body
 * that carries them and must test-compare against these tuples.
 *
 * Binding invariants these encode (plan §3):
 *  - I3/I6: a match result's `provenance` is the only thing that can make it
 *    eligible for auto-accept, and only `t1_exact` ever qualifies. A model-
 *    selected candidate (`t3_llm_rerank`) is a candidate, never a resolution.
 *  - I4: allergen relevance is tri-state and `unknown` is TREATED AS RELEVANT
 *    by every gate and UI -- there is deliberately no boolean form of it.
 *  - I5: `user_promoted` aliases never confer auto-accept.
 */

/** How an intake string was resolved against the curated Ingredient catalogue. */
export const MATCH_PROVENANCE_VALUES = [
  "t1_exact",
  "t2_fuzzy",
  "t3_llm_rerank",
  "unmatched",
] as const;
export type MatchProvenance = (typeof MATCH_PROVENANCE_VALUES)[number];

/**
 * Tri-state allergen relevance of ONE intake row against the requesting
 * household's declared allergies. `unknown` (unmatched or unassessable rows)
 * is treated exactly like `relevant` everywhere -- never as safe.
 */
export const ALLERGEN_RELEVANCE_VALUES = [
  "relevant",
  "not-relevant",
  "unknown",
] as const;
export type AllergenRelevance = (typeof ALLERGEN_RELEVANCE_VALUES)[number];

/** Who vouched for the alias a `t1_exact` hit came through. */
export const ALIAS_PROVENANCE_VALUES = ["curated", "user_promoted"] as const;
export type AliasProvenance = (typeof ALIAS_PROVENANCE_VALUES)[number];

/**
 * Family of a scanned/typed code, decided BEFORE any lookup (slice S5).
 * `variable-weight` (prefix 2) never yields an identity -- quantity/price only.
 */
export const BARCODE_FAMILY_VALUES = [
  "gtin",
  "plu",
  "variable-weight",
  "unknown",
] as const;
export type BarcodeFamily = (typeof BARCODE_FAMILY_VALUES)[number];

/** Deterministic receipt-consistency flags (slice S7). Flags never drop rows. */
export const RECEIPT_FLAG_VALUES = [
  "items_vs_subtotal_mismatch",
  "subtotal_tax_vs_total_mismatch",
] as const;
export type ReceiptFlag = (typeof RECEIPT_FLAG_VALUES)[number];

/**
 * The single provenance value that may ever satisfy auto-accept (I3). Kept
 * here so `canAutoAccept` (slice S3a) and its tests reference one constant
 * rather than a string literal.
 */
export const AUTO_ACCEPT_ELIGIBLE_PROVENANCE: MatchProvenance = "t1_exact";
